/**
 * deepspace kill [--port N] [--all]
 *
 * Stops a local dev server bound to the given port, plus the workerd
 * child processes that wrangler/Vite would normally clean up but
 * sometimes leak when the parent dies ungracefully (Ctrl-C in a
 * detached terminal, IDE close, sandbox deny on signal, etc).
 *
 *   deepspace kill                  # kill listener on 5173 + its workerd children
 *   deepspace kill --port 5180      # kill listener on a different port
 *   deepspace kill --all            # also sweep ALL stray workerd/wrangler procs on the machine
 *
 * Cross-platform: uses lsof/pgrep on macOS and Linux, and PowerShell
 * (Get-NetTCPConnection / Get-CimInstance) on Windows. PowerShell ships
 * with every supported Windows version since 7; no extra install required.
 *
 * Always sends SIGTERM first, waits briefly, then SIGKILL anything
 * still alive — same shape as `vite`'s own shutdown handler so we
 * don't trample DO state mid-write. (On Windows, SIGTERM is best-effort:
 * Win32 has no real signals, so node maps both to TerminateProcess. We
 * still issue them in the same order for consistent log output.)
 */

import { defineCommand } from 'citty'
import { spawnSync } from 'node:child_process'
import { setTimeout as wait } from 'node:timers/promises'

const DEFAULT_PORT = 5173
const SIGTERM_GRACE_MS = 1500
const IS_WIN = process.platform === 'win32'

export default defineCommand({
  meta: {
    name: 'kill',
    description: 'Kill the local dev server and any orphaned workerd processes',
  },
  args: {
    port: {
      type: 'string',
      description: `Port the dev server is bound to (default ${DEFAULT_PORT}, or $DEEPSPACE_PORT)`,
      required: false,
    },
    all: {
      type: 'boolean',
      description: 'Also kill stray workerd/wrangler processes across all ports',
      default: false,
    },
  },
  async run({ args }) {
    const port = resolvePort(args.port)
    const targets = new Set<number>()

    // 1. Anything LISTENing on the dev port (vite + the workerd child it
    //    publishes through, occasionally a stray inspector).
    for (const pid of listenerPids(port)) targets.add(pid)

    // 2. Children of those listeners — wrangler/vite tend to spawn workerd
    //    as a sibling, but in some shells it ends up as a child instead.
    for (const pid of [...targets]) {
      for (const child of childPids(pid)) targets.add(child)
    }

    // 3. With --all, sweep workerd / wrangler / vite anywhere on the box.
    //    Useful when port 5173 is "free" but a leftover workerd from a
    //    previous run is still holding D1 / DO state in .wrangler/.
    if (args.all) {
      for (const pid of processPidsByName(['workerd', 'wrangler', 'vite'])) {
        targets.add(pid)
      }
    }

    if (targets.size === 0) {
      console.log(
        args.all
          ? `Nothing listening on :${port} and no stray workerd/wrangler/vite processes found.`
          : `Nothing listening on :${port}. (Pass --all to sweep stray workerd/wrangler.)`,
      )
      return
    }

    // SIGTERM pass.
    for (const pid of targets) {
      if (sendSignal(pid, 'SIGTERM')) {
        console.log(`SIGTERM → pid ${pid}`)
      }
    }
    await wait(SIGTERM_GRACE_MS)

    const stillAlive: number[] = []
    for (const pid of targets) {
      if (isAlive(pid)) stillAlive.push(pid)
    }

    // SIGKILL holdouts.
    for (const pid of stillAlive) {
      if (sendSignal(pid, 'SIGKILL')) {
        console.log(`SIGKILL → pid ${pid} (did not exit on SIGTERM)`)
      }
    }

    // Re-verify the port is free.
    await wait(200)
    const remaining = listenerPids(port)
    if (remaining.length > 0) {
      console.error(
        `Port ${port} is still held by pid(s) ${remaining.join(', ')} — try \`deepspace kill --port ${port}\` again, or kill manually.`,
      )
      process.exit(1)
    }

    console.log(`Port ${port} is free.`)
  },
})

function resolvePort(arg?: string): number {
  const raw = arg ?? process.env.DEEPSPACE_PORT
  if (!raw) return DEFAULT_PORT
  const n = Number(raw)
  if (!Number.isInteger(n) || n < 1 || n > 65535) {
    console.error(`Invalid port: ${raw}. Must be an integer between 1 and 65535.`)
    process.exit(1)
  }
  return n
}

// ── Platform-specific helpers ─────────────────────────────────────────────

function listenerPids(port: number): number[] {
  if (IS_WIN) {
    // Get-NetTCPConnection is the modern replacement for netstat -ano.
    // -ErrorAction SilentlyContinue swallows "No matching connection" so
    // the command exits 0 with empty stdout instead of a noisy red error.
    return runPowershell(
      `Get-NetTCPConnection -LocalPort ${port} -State Listen -ErrorAction SilentlyContinue | Select-Object -ExpandProperty OwningProcess`,
    )
  }
  // -t prints PIDs only, one per line; -nP avoids DNS / service lookups.
  return run(['lsof', '-nP', `-iTCP:${port}`, '-sTCP:LISTEN', '-t'])
}

function childPids(parentPid: number): number[] {
  if (IS_WIN) {
    return runPowershell(
      `Get-CimInstance Win32_Process -Filter "ParentProcessId=${parentPid}" | Select-Object -ExpandProperty ProcessId`,
    )
  }
  return run(['pgrep', '-P', String(parentPid)])
}

function processPidsByName(names: string[]): number[] {
  const all = new Set<number>()
  if (IS_WIN) {
    // Match against process Name (e.g. "workerd") AND CommandLine (catches
    // npx-launched scripts like "vite" that show up as node.exe with vite
    // in the args).
    const orClauses = names
      .map((n) => `($_.Name -like '*${n}*') -or ($_.CommandLine -like '*${n}*')`)
      .join(' -or ')
    const script = `Get-CimInstance Win32_Process | Where-Object { ${orClauses} } | Select-Object -ExpandProperty ProcessId`
    for (const pid of runPowershell(script)) {
      if (pid !== process.pid) all.add(pid)
    }
    return [...all]
  }
  for (const name of names) {
    for (const pid of run(['pgrep', '-f', name])) {
      if (pid !== process.pid) all.add(pid)
    }
  }
  return [...all]
}

// ── Process primitives ────────────────────────────────────────────────────

/** Run a command and return whitespace-separated PIDs (empty array on failure). */
function run(cmd: string[]): number[] {
  try {
    const r = spawnSync(cmd[0], cmd.slice(1), { encoding: 'utf-8' })
    if (r.status !== 0) return []
    return parsePidLines(r.stdout)
  } catch {
    return []
  }
}

/**
 * Run a PowerShell snippet. Prefers `pwsh` (PowerShell 7+) when available
 * and falls back to `powershell.exe` (Windows PowerShell 5.1, ships with
 * every Windows version we care about).
 */
function runPowershell(script: string): number[] {
  const exe = pwshOrPowershell()
  try {
    const r = spawnSync(
      exe,
      ['-NoProfile', '-NonInteractive', '-Command', script],
      { encoding: 'utf-8' },
    )
    if (r.status !== 0) return []
    return parsePidLines(r.stdout)
  } catch {
    return []
  }
}

function pwshOrPowershell(): string {
  // `pwsh` is the modern cross-platform binary; `powershell.exe` is the
  // legacy Windows-only one. Try modern first, fall back if missing.
  const probe = spawnSync('pwsh', ['-NoProfile', '-Command', '$PSVersionTable.PSVersion.Major'], {
    encoding: 'utf-8',
  })
  if (probe.status === 0) return 'pwsh'
  return 'powershell.exe'
}

function parsePidLines(stdout: string): number[] {
  return stdout
    .split(/\s+/)
    .map((s) => Number.parseInt(s, 10))
    .filter((n) => Number.isFinite(n) && n > 0)
}

function sendSignal(pid: number, signal: NodeJS.Signals): boolean {
  try {
    process.kill(pid, signal)
    return true
  } catch {
    return false
  }
}

function isAlive(pid: number): boolean {
  try {
    // signal 0 = "do nothing, just check"
    process.kill(pid, 0)
    return true
  } catch {
    return false
  }
}
